import { createReducer, on } from '@ngrx/store';

import { GlobalBookingTravelClass, LocalDate, TripType, setIn, updateIn } from '@fcom/core';

import { GlobalBookingActions } from '../actions';
import { GlobalBookingState, LocationPair } from '../store.interface';
import { MAX_PAX_AMOUNT, MIN_LEAD_PAX_AMOUNT } from '../constants';

/**
 * Helper function to manage the locations based on tripType.
 * The locations are turned into flights when creating the deeplink
 * and the rest of the flow will handle them in that manner,
 * so here we'll transform the locations to match the handling in the rest of the flows
 *
 * @param locations An array of location pairs e.g. [{ origin, destination }]
 * @param tripType Given tripType
 * @returns Formatted list of locations based on the given tripType
 */
const updateLocations = (locations: LocationPair[], tripType: TripType) => {
  switch (tripType) {
    case TripType.ONEWAY:
      // For oneway trips we only pass one location object === one flight
      return [locations[0]];
    case TripType.RETURN:
      // For round trips we mirror the selected location to be (backwards) compatible with how the rest of the flow is handling locations
      return [locations[0], { origin: locations[0].destination, destination: locations[0].origin }];
    case TripType.MULTICITY:
      // For multi-city we add an empty second location if only one location exists.
      // This is to correctly render the location selectors in the booking-widget.
      return locations.length < 2 ? [locations[0], {}] : locations;
    default:
      return locations;
  }
};

export const initialState: GlobalBookingState = {
  dates: {
    departureDate: undefined,
    returnDate: undefined,
  },
  tripType: TripType.RETURN,
  travelClasses: {
    selected: GlobalBookingTravelClass.MIXED,
    availability: {
      ECONOMY: true,
      BUSINESS: true,
      FIRST: false,
      ECOPREMIUM: false,
      MIXED: false,
    },
  },
  origin: undefined,
  routeType: undefined,
  paxAmount: {
    adults: 1,
    c15s: 0,
    children: 0,
    infants: 0,
  },
  locations: [{ origin: undefined, destination: undefined }],
};

const removeInvalidMultiCityOriginsAndDestinations = (
  allLocationPairs: LocationPair[],
  updateLocation: LocationPair,
  updateIndex: number
): LocationPair[] =>
  allLocationPairs
    .map((loc, i) => (i === updateIndex ? { ...loc, ...updateLocation } : loc))
    .map((baseLocationPair, i, locationPairs) => {
      if (i > 0) {
        const previousLocationPair = locationPairs[i - 1];
        const firtsLocationPair = locationPairs[0];
        const hasInvalidOrigin =
          i <= locationPairs.length - 1 &&
          baseLocationPair?.origin?.continentCode !== previousLocationPair?.destination?.continentCode;
        const hasInvalidDestination =
          i === locationPairs.length - 1 &&
          baseLocationPair?.destination?.continentCode !== firtsLocationPair?.origin?.continentCode;
        return {
          ...baseLocationPair,
          ...(hasInvalidOrigin && { ['origin']: undefined }),
          ...(hasInvalidDestination && { ['destination']: undefined }),
        };
      }

      return baseLocationPair;
    });

const globalBooking = createReducer(
  initialState,
  on(GlobalBookingActions.setDates, (state, { dates }) => ({
    ...state,
    dates,
  })),
  on(GlobalBookingActions.setTripType, (state, { tripType }) => ({
    ...state,
    tripType,
    dates: tripType === TripType.ONEWAY ? { ...state.dates, returnDate: undefined } : state.dates,
    locations: updateLocations(state.locations, tripType),
  })),
  on(GlobalBookingActions.setTravelClass, (state, { travelClass }) => ({
    ...state,
    travelClasses: { ...state.travelClasses, selected: travelClass },
  })),
  on(GlobalBookingActions.setOrigin, (state, { origin }) => ({
    ...state,
    origin,
  })),
  on(GlobalBookingActions.setRouteType, (state, { routeType }) => ({
    ...state,
    routeType,
  })),
  on(GlobalBookingActions.setPaxAmount, (state, { paxAmount }) => ({
    ...state,
    paxAmount,
  })),
  on(GlobalBookingActions.increasePaxAmountField, (state, { field, increment }) =>
    updatePaxAmountField(state, field, (old) => old + increment)
  ),
  on(GlobalBookingActions.decreasePaxAmountField, (state, { field, decrement }) =>
    updatePaxAmountField(state, field, (old) => Math.max(old - decrement, 0))
  ),
  on(GlobalBookingActions.setDiscountCode, (state, { discountCode }) => ({
    ...state,
    discountCode,
  })),
  on(GlobalBookingActions.setSelection, (state, { selection }) => ({
    ...state,
    paxAmount: selection.paxAmount,
    locations: [
      {
        origin: selection?.origin,
        destination: selection?.destination,
      },
    ],
    tripType: selection.tripType,
    travelClasses: { ...state.travelClasses, selected: selection.travelClass },
    dates:
      selection.tripType === TripType.ONEWAY
        ? { departureDate: new LocalDate(selection.travelDates.departureDate), returnDate: undefined }
        : {
            departureDate: new LocalDate(selection.travelDates.departureDate),
            returnDate: new LocalDate(selection.travelDates.returnDate),
          },
    discountCode: selection.discountCode ?? undefined,
  })),
  on(GlobalBookingActions.resetSelection, (state) => ({
    ...state,
    paxAmount: initialState.paxAmount,
    locations: [
      {
        origin: state.locations[0].origin,
      },
    ],
    tripType: initialState.tripType,
    travelClasses: initialState.travelClasses,
    dates: initialState.dates,
    discountCode: initialState.discountCode,
  })),
  on(GlobalBookingActions.setLocations, (state, { locations }) => ({
    ...state,
    locations: updateLocations(locations, state.tripType),
  })),
  on(GlobalBookingActions.updateLocationPair, (state, { locationPair, index }) => {
    return {
      ...state,
      locations:
        state.tripType === TripType.MULTICITY
          ? removeInvalidMultiCityOriginsAndDestinations(state.locations, locationPair, index)
          : state.locations.map((loc, i) => (i === index ? { ...loc, ...locationPair } : loc)),
    };
  }),
  // This was moved here from booking reducer, is it even relevant?
  // Should we move PaymentActions and PaymentStatus under common?
  // Data Clean-ups: When Payment verified
  on(GlobalBookingActions.paymentVerified, (state) => ({
    ...state,
    locations: [{ origin: state.locations[0].origin }],
    dates: initialState.dates,
  }))
);

const updatePaxAmountField = (state: GlobalBookingState, field: string, mutator: (oldValue: number) => number) => {
  const newState = disallowMoreInfantsThanAdults(updateIn(state, ['paxAmount', field], mutator));
  const newLeadPaxAmount = newState?.paxAmount?.adults;
  const newTotal = newLeadPaxAmount + state?.paxAmount?.c15s + state?.paxAmount?.children;
  if (newTotal > MAX_PAX_AMOUNT || newLeadPaxAmount < MIN_LEAD_PAX_AMOUNT) {
    return state;
  }
  return newState;
};

const disallowMoreInfantsThanAdults = (state: GlobalBookingState) => {
  const numAdults = state.paxAmount.adults;
  if (state.paxAmount.infants > numAdults) {
    return setIn(state, 'paxAmount.infants', numAdults);
  }
  return state;
};

export const globalBookingReducer = (
  state: GlobalBookingState,
  action: GlobalBookingActions.GlobalBookingActionsUnion
): GlobalBookingState => {
  return globalBooking(state, action);
};
